import { useEffect } from "react";
import { Card, CardBody, Flex, Spinner, Stack } from "@chakra-ui/react";
import { formatDistanceToNow } from "date-fns";
import CDS from "design-system";
import { useNavigate, useParams } from "react-router";

import NoSidebarScrollablePage from "layouts/NoSidebarScrollablePage";
import { useMyFormAssignmentsQuery } from "features/Resources/CustomForm/hooks";
import { isTaskOpen } from "features/Resources/Task/helpers";

function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <NoSidebarScrollablePage>
      <Card>
        <CardBody>
          <Stack spacing={3} align="center" py={6} textAlign="center">
            {children}
          </Stack>
        </CardBody>
      </Card>
    </NoSidebarScrollablePage>
  );
}

export default function FormShareResolverPage() {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { data: tasks, isLoading } = useMyFormAssignmentsQuery(uuid);

  const openTasks = (tasks ?? []).filter(isTaskOpen);
  const completedTasks = (tasks ?? []).filter((task) => task.status === "completed");

  useEffect(() => {
    if (isLoading) return;
    if (openTasks.length === 1) {
      navigate(`/form/${openTasks[0].id}`, { replace: true });
    }
  }, [isLoading, openTasks, navigate]);

  if (isLoading) {
    return (
      <NoSidebarScrollablePage>
        <Flex justify="center" p={12}>
          <Spinner />
        </Flex>
      </NoSidebarScrollablePage>
    );
  }

  if (openTasks.length === 1) {
    return (
      <NoSidebarScrollablePage>
        <Flex justify="center" p={12}>
          <Spinner />
        </Flex>
      </NoSidebarScrollablePage>
    );
  }

  if (openTasks.length >= 2) {
    return (
      <NoSidebarScrollablePage>
        <Card>
          <CardBody>
            <Stack spacing={4} py={2}>
              <CDS.Heading20>Which one would you like to fill out?</CDS.Heading20>
              <Stack spacing={2}>
                {openTasks.map((task) => (
                  <Flex
                    key={task.id}
                    as="button"
                    onClick={() => navigate(`/form/${task.id}`)}
                    align="center"
                    justify="space-between"
                    borderWidth="1px"
                    borderColor="border.subtle"
                    borderRadius="md"
                    px={4}
                    py={3}
                    _hover={{ borderColor: "cdsIndigo.500", bg: "bg.tertiary" }}
                  >
                    <CDS.Text14 fontWeight="bold">{task.name}</CDS.Text14>
                    {task.createdAt && (
                      <CDS.Text12 color="content.tertiary">
                        Assigned {formatDistanceToNow(new Date(String(task.createdAt)), { addSuffix: true })}
                      </CDS.Text12>
                    )}
                  </Flex>
                ))}
              </Stack>
            </Stack>
          </CardBody>
        </Card>
      </NoSidebarScrollablePage>
    );
  }

  if (completedTasks.length > 0) {
    const latestCompleted = completedTasks[0];
    return (
      <CenteredCard>
        <CDS.Heading20>You&apos;ve already submitted this form</CDS.Heading20>
        {latestCompleted?.statusUpdatedAt && (
          <CDS.Text14 color="fg.subtle">
            Submitted {formatDistanceToNow(new Date(String(latestCompleted.statusUpdatedAt)), { addSuffix: true })}
          </CDS.Text14>
        )}
      </CenteredCard>
    );
  }

  // Nothing open and nothing submitted (no assignment, or only skipped/removed) — nothing to do here.
  return (
    <CenteredCard>
      <CDS.Heading20>You don&apos;t have this form assigned</CDS.Heading20>
      <CDS.Text14 color="fg.subtle">
        Ask whoever shared this link with you to assign it to you first.
      </CDS.Text14>
    </CenteredCard>
  );
}
